export const DEFAULT_MEETING_TIMEZONE = 'Asia/Kolkata';
export const MIN_MANUAL_MEETING_MINUTES = 15;
export const DEFAULT_MANUAL_MEET_DURATION = '30';
export const RESCHEDULE_START_TIME_STEP_MINUTES = 15;

/** Common IANA timezones for manual scheduling and availability forms. */
export const MEETING_TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'America/New_York', label: 'America/New_York (ET)' },
  { value: 'America/Chicago', label: 'America/Chicago (CT)' },
  { value: 'America/Denver', label: 'America/Denver (MT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PT)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
];

/** Manual Live Meet length presets (end time is derived from start + duration). */
export const MANUAL_MEET_DURATION_OPTIONS = [
  { value: '15', label: '15 min', minutes: 15 },
  { value: '30', label: '30 min', minutes: 30 },
  { value: '45', label: '45 min', minutes: 45 },
  { value: '60', label: '1 hour', minutes: 60 },
  { value: '90', label: '1.5 hour', minutes: 90 },
  { value: '120', label: '2 hour', minutes: 120 },
  { value: '480', label: '8 hour', minutes: 480 },
];

export function getManualMeetDurationLabel(durationValue) {
  const preset = MANUAL_MEET_DURATION_OPTIONS.find(
    (option) => option.value === String(durationValue || '')
  );
  if (preset?.label) return preset.label;
  const minutes = Number(durationValue);
  if (Number.isFinite(minutes) && minutes > 0) {
    if (minutes % 60 === 0) {
      const hours = minutes / 60;
      return hours === 1 ? '1 hour' : `${hours} hours`;
    }
    return `${minutes} min`;
  }
  return '';
}

/** Resolve host-controlled slot length (minutes) from availability or a booking window. */
export function resolveHostSlotDurationMinutes(
  source,
  fallbackMinutes = Number(DEFAULT_MANUAL_MEET_DURATION) || 30
) {
  let raw = source;
  if (source && typeof source === 'object') {
    raw =
      source.slot_duration_minutes ??
      source.slotDurationMinutes ??
      source.duration_minutes;
    if (
      raw == null &&
      source.scheduled_start_time &&
      source.scheduled_end_time
    ) {
      const start = new Date(source.scheduled_start_time);
      const end = new Date(source.scheduled_end_time);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start) {
        raw = Math.round((end.getTime() - start.getTime()) / 60000);
      }
    }
  }
  const minutes = Number(raw);
  if (Number.isFinite(minutes) && minutes >= MIN_MANUAL_MEETING_MINUTES) {
    return Math.round(minutes);
  }
  const fallback = Number(fallbackMinutes);
  return Number.isFinite(fallback) && fallback >= MIN_MANUAL_MEETING_MINUTES
    ? Math.round(fallback)
    : MIN_MANUAL_MEETING_MINUTES;
}

export function resolveHostSlotDurationValue(source, fallbackMinutes) {
  const minutes = resolveHostSlotDurationMinutes(source, fallbackMinutes);
  const matched = MANUAL_MEET_DURATION_OPTIONS.find((item) => item.minutes === minutes);
  return matched?.value || String(minutes);
}

export function formatHostSlotStepLabel(minutes) {
  const label = getManualMeetDurationLabel(String(minutes));
  return label ? `${label} slots` : `${minutes}-minute slots`;
}

export function addMinutesToTimeValue(timeStr, minutes) {
  const time = normalizeTimeValue(timeStr);
  if (!time || !Number.isFinite(minutes)) return '';
  const [hour, minute] = time.split(':').map(Number);
  const totalMinutes = hour * 60 + minute + minutes;
  if (totalMinutes >= 24 * 60) {
    return '23:59';
  }
  const nextHour = Math.floor(totalMinutes / 60);
  const nextMinute = totalMinutes % 60;
  return `${String(nextHour).padStart(2, '0')}:${String(nextMinute).padStart(2, '0')}`;
}

export function resolveManualMeetEndTime({ startTime, durationValue }) {
  const start = normalizeTimeValue(startTime);
  if (!start) return '';
  const option = MANUAL_MEET_DURATION_OPTIONS.find(
    (item) => item.value === String(durationValue || '')
  );
  const minutes = option?.minutes ?? Number(durationValue);
  if (!Number.isFinite(minutes) || minutes < MIN_MANUAL_MEETING_MINUTES) return '';
  return addMinutesToTimeValue(start, minutes);
}

export function resolveManualMeetDurationValue({ startTime, endTime }) {
  const start = normalizeTimeValue(startTime);
  const end = normalizeTimeValue(endTime);
  if (!start || !end) return '';
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);
  const diffMinutes = endHour * 60 + endMinute - (startHour * 60 + startMinute);
  if (diffMinutes <= 0) return '';
  const matched = MANUAL_MEET_DURATION_OPTIONS.find((item) => item.minutes === diffMinutes);
  return matched?.value || String(diffMinutes);
}

const RESCHEDULE_MONTHS = {
  january: 1,
  jan: 1,
  february: 2,
  feb: 2,
  march: 3,
  mar: 3,
  april: 4,
  apr: 4,
  may: 5,
  june: 6,
  jun: 6,
  july: 7,
  jul: 7,
  august: 8,
  aug: 8,
  september: 9,
  sep: 9,
  sept: 9,
  october: 10,
  oct: 10,
  november: 11,
  nov: 11,
  december: 12,
  dec: 12,
};

const RESCHEDULE_TIMEZONE_ALIASES = {
  IST: 'Asia/Kolkata',
  UTC: 'UTC',
  GMT: 'UTC',
  EST: 'America/New_York',
  EDT: 'America/New_York',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
};

export function resolveRescheduleTimezoneName(value, fallback = DEFAULT_MEETING_TIMEZONE) {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  if (raw.includes('/')) {
    return isValidIanaTimezone(raw) ? raw : fallback;
  }
  return RESCHEDULE_TIMEZONE_ALIASES[raw.toUpperCase()] || fallback;
}

export function normalizeRescheduleTimeLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const twentyFourHour = raw.match(/^(\d{1,2}):(\d{2})/);
  if (twentyFourHour) {
    const hour = Math.min(23, Math.max(0, Number(twentyFourHour[1])));
    const minute = Math.min(59, Math.max(0, Number(twentyFourHour[2])));
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }
  const twelveHour = raw.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (twelveHour) {
    let hour = Number(twelveHour[1]) % 12;
    if (twelveHour[3].toUpperCase() === 'PM') hour += 12;
    return `${String(hour).padStart(2, '0')}:${twelveHour[2]}`;
  }
  return normalizeTimeValue(raw);
}

export function parseRescheduleSuggestionLabel(
  label,
  defaultTimezone = DEFAULT_MEETING_TIMEZONE
) {
  const text = String(label || '').trim();
  if (!text) return null;

  let timezone = resolveRescheduleTimezoneName(defaultTimezone, defaultTimezone);
  let core = text;
  const tzMatch = text.match(/\(([^)]+)\)\s*$/);
  if (tzMatch) {
    timezone = resolveRescheduleTimezoneName(tzMatch[1], defaultTimezone);
    core = text.slice(0, tzMatch.index).trim();
  }

  let datePart = '';
  let timePart = '';
  if (core.includes('·')) {
    [datePart, timePart] = core.split('·', 2);
  } else if (core.includes('|')) {
    [datePart, timePart] = core.split('|', 2);
  } else {
    return { label: text, timezone };
  }

  datePart = datePart
    .replace(/^(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*,?\s*/i, '')
    .trim()
    .replace(/^,|,$/g, '')
    .trim();
  timePart = String(timePart || '').trim();

  const dateMatch = datePart.match(/(\d{1,2})\s+([A-Za-z]+),?\s+(\d{4})/);
  if (!dateMatch) {
    return { label: text, timezone };
  }

  const day = Number(dateMatch[1]);
  const month = RESCHEDULE_MONTHS[dateMatch[2].toLowerCase()];
  const year = Number(dateMatch[3]);
  if (!month) {
    return { label: text, timezone };
  }

  const rangeMatch = timePart.match(/([^–\-]+)[–\-](.+)/);
  let startTime = '';
  let endTime = '';
  if (rangeMatch) {
    startTime = normalizeRescheduleTimeLabel(rangeMatch[1]);
    endTime = normalizeRescheduleTimeLabel(rangeMatch[2]);
  } else {
    startTime = normalizeRescheduleTimeLabel(timePart);
  }

  if (!startTime) {
    return { label: text, timezone };
  }

  return {
    label: text,
    date: `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    start_time: startTime,
    end_time: endTime,
    timezone,
  };
}

export function isValidIanaTimezone(timeZone) {
  const value = String(timeZone || '').trim();
  if (!value) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

export function getTodayDateString() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getMinTimeForDate(dateStr) {
  if (!dateStr || dateStr !== getTodayDateString()) return '';
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

/** Normalize `<input type="time">` value to HH:mm (handles HH:mm:ss). */
export function normalizeTimeValue(timeStr) {
  const raw = String(timeStr || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  const hour = Math.min(23, Math.max(0, Number(match[1])));
  const minute = Math.min(59, Math.max(0, Number(match[2])));
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function timeValueToMinutes(timeStr) {
  const time = normalizeTimeValue(timeStr);
  if (!time) return null;
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
}

export function minutesToTimeValue(totalMinutes) {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, totalMinutes));
  const hour = Math.floor(clamped / 60);
  const minute = clamped % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function snapTimeToStep(
  timeStr,
  stepMinutes = RESCHEDULE_START_TIME_STEP_MINUTES,
  mode = 'nearest'
) {
  const minutes = timeValueToMinutes(timeStr);
  if (minutes == null || !stepMinutes) return normalizeTimeValue(timeStr);
  if (mode === 'ceil') {
    return minutesToTimeValue(Math.ceil(minutes / stepMinutes) * stepMinutes);
  }
  if (mode === 'floor') {
    return minutesToTimeValue(Math.floor(minutes / stepMinutes) * stepMinutes);
  }
  return minutesToTimeValue(Math.round(minutes / stepMinutes) * stepMinutes);
}

export function formatRescheduleTimeLabel(timeStr) {
  const time = normalizeTimeValue(timeStr);
  if (!time) return '';
  const [hour, minute] = time.split(':').map(Number);
  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatRescheduleDateLabel(dateValue) {
  const raw = String(dateValue || '').trim();
  if (!raw) return '';
  const [year, month, day] = raw.split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatRescheduleTimezoneShort(timezone = DEFAULT_MEETING_TIMEZONE) {
  const zone = String(timezone || DEFAULT_MEETING_TIMEZONE).trim();
  const option = MEETING_TIMEZONE_OPTIONS.find((item) => item.value === zone);
  if (option?.label?.includes('(IST)')) return 'IST';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((part) => part.type === 'timeZoneName')?.value || zone;
  } catch {
    return zone;
  }
}

export function formatRescheduleSlotDisplay(
  slot,
  timezone = DEFAULT_MEETING_TIMEZONE
) {
  if (!slot?.date || !slot?.startTime) return 'Not set';
  const endTime =
    normalizeTimeValue(slot.endTime) ||
    resolveManualMeetEndTime({
      startTime: slot.startTime,
      durationValue: slot.duration || DEFAULT_MANUAL_MEET_DURATION,
    });
  const dateLabel = formatRescheduleDateLabel(slot.date);
  const startLabel = formatRescheduleTimeLabel(slot.startTime);
  const endLabel = endTime ? formatRescheduleTimeLabel(endTime) : '';
  const tzLabel = formatRescheduleTimezoneShort(timezone);
  const timeLabel = endLabel ? `${startLabel} – ${endLabel}` : startLabel;
  return `${dateLabel} · ${timeLabel} · ${tzLabel}`;
}

export function buildRescheduleStartTimeOptions({
  stepMinutes = RESCHEDULE_START_TIME_STEP_MINUTES,
  minTime = '',
  startHour = 0,
  endHour = 24,
} = {}) {
  const minMinutes = timeValueToMinutes(minTime);
  const options = [];
  for (let minutes = startHour * 60; minutes < endHour * 60; minutes += stepMinutes) {
    if (minMinutes != null && minutes < minMinutes) continue;
    const value = minutesToTimeValue(minutes);
    options.push({
      value,
      label: formatRescheduleTimeLabel(value),
    });
  }
  return options;
}

export function buildRescheduleEndTimeOptions({
  startTime,
  stepMinutes = RESCHEDULE_START_TIME_STEP_MINUTES,
  minDurationMinutes = MIN_MANUAL_MEETING_MINUTES,
  endHour = 24,
} = {}) {
  const startMinutes = timeValueToMinutes(startTime);
  if (startMinutes == null) return [];
  const minEndMinutes = startMinutes + minDurationMinutes;
  const options = [];
  for (let minutes = minEndMinutes; minutes < endHour * 60; minutes += stepMinutes) {
    const value = minutesToTimeValue(minutes);
    options.push({
      value,
      label: formatRescheduleTimeLabel(value),
    });
  }
  return options;
}

/**
 * Wall-clock date/time in an IANA timezone → UTC ISO string (…Z) for the API.
 */
export function toIsoInTimezone(dateStr, timeStr, timeZone) {
  const date = String(dateStr || '').trim();
  const time = normalizeTimeValue(timeStr);
  const zone = String(timeZone || DEFAULT_MEETING_TIMEZONE).trim();
  if (!date || !time || !isValidIanaTimezone(zone)) return '';
  const temporalApi = typeof window !== 'undefined' ? window.Temporal : undefined;

  if (temporalApi) {
    try {
      const zdt = temporalApi.ZonedDateTime.from(`${date}T${time}:00[${zone}]`);
      return zdt.toInstant().toString();
    } catch {
      return '';
    }
  }

  return wallClockToUtcIso(date, time, zone);
}

/** Convert wall time in `timeZone` to UTC ISO without Temporal. */
function wallClockToUtcIso(dateStr, timeStr, timeZone) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) return '';

  const targetUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  let guess = targetUtc;
  for (let i = 0; i < 4; i += 1) {
    const parts = Object.fromEntries(
      formatter.formatToParts(new Date(guess)).map((p) => [p.type, p.value])
    );
    const shownUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    const delta = targetUtc - shownUtc;
    if (delta === 0) break;
    guess += delta;
  }

  return new Date(guess).toISOString();
}

/** Parse ISO / Temporal strings for validation comparisons. */
export function parseIsoToMs(iso) {
  if (!iso) return NaN;
  const normalized = String(iso)
    .trim()
    .replace(/\[.*\]$/, '');
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? NaN : ms;
}

export function validateRescheduleTimeWindow({
  startIso,
  endIso,
  minDurationMinutes = 1,
}) {
  return validateManualScheduleWindow({
    startIso,
    endIso,
    minDurationMinutes,
  });
}

export function validateManualScheduleWindow({
  startIso,
  endIso,
  minDurationMinutes = MIN_MANUAL_MEETING_MINUTES,
}) {
  const startMs = parseIsoToMs(startIso);
  const endMs = parseIsoToMs(endIso);
  const nowMs = Date.now();

  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return 'Invalid date or time.';
  }
  if (startMs <= nowMs) {
    return 'Start time must be in the future.';
  }
  if (endMs <= startMs) {
    return 'End time must be after start time.';
  }
  const durationMin = (endMs - startMs) / 60000;
  if (durationMin < minDurationMinutes) {
    return `Meeting must be at least ${minDurationMinutes} minutes long.`;
  }
  return '';
}

export function validateRescheduleWithinAvailability({
  startIso,
  endIso,
  availabilityWindows = [],
  timezone = DEFAULT_MEETING_TIMEZONE,
}) {
  const windows = (availabilityWindows || []).filter(Boolean);
  if (!windows.length) {
    return '';
  }

  const startMs = parseIsoToMs(startIso);
  const endMs = parseIsoToMs(endIso);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return 'Invalid date or time.';
  }

  const resolveWindowBoundsMs = (window) => {
    const windowTimezone = resolveRescheduleTimezoneName(
      window?.timezone || timezone,
      timezone
    );
    const startValue =
      window?.slot_value ||
      (window?.date && window?.start_time
        ? toIsoInTimezone(window.date, window.start_time, windowTimezone)
        : '');
    const endValue =
      window?.end_slot_value ||
      (window?.date && window?.end_time
        ? toIsoInTimezone(window.date, window.end_time, windowTimezone)
        : '');
    return {
      startMs: parseIsoToMs(startValue),
      endMs: parseIsoToMs(endValue),
    };
  };

  const fitsWindow = windows.some((window) => {
    const { startMs: windowStartMs, endMs: windowEndMs } = resolveWindowBoundsMs(window);
    if (Number.isNaN(windowStartMs) || Number.isNaN(windowEndMs)) {
      return false;
    }
    return startMs >= windowStartMs && endMs <= windowEndMs;
  });

  if (!fitsWindow) {
    return 'Pick a final interview time within the shared availability window.';
  }
  return '';
}

export function createClientRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `meet-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
